import type {
  AfterViewInit,
  OnChanges,
  OnDestroy,
  SimpleChanges,
} from '@angular/core';
import {
  ChangeDetectionStrategy,
  Component,
  ContentChild,
  ContentChildren,
  EventEmitter,
  HostBinding,
  Input,
  Output,
  QueryList,
  TemplateRef,
} from '@angular/core';
import { Currency } from '@freelancer/datastore/collections';
import { isImageFile } from '@freelancer/ui';
import { FileDisplayType } from '@freelancer/ui/file-display';
import {
  IconBackdrop,
  IconBackdropStyle,
  IconColor,
  IconSize,
} from '@freelancer/ui/icon';
import { LinkUnderline } from '@freelancer/ui/link';
import {
  AspectRatioValue,
  PictureDisplay,
  PictureLayout,
  PictureLoadMethod,
  PictureMetadata,
  PictureObjectFit,
} from '@freelancer/ui/picture';
import { BehaviorSubject, Subscription } from 'rxjs';
import { ShowcaseCardVariant } from './showcase-card.types';

@Component({
  standalone: false,
  selector: 'fl-showcase-card-title',
  template: `
    <div class="Heading">
      <p
        class="text-body-16 font-medium {{
          titlePrice ? 'mr-xxsmall' : 'mr-none'
        }}"
        [flMaxLines]="maxLines"
      >
        <ng-content></ng-content>
      </p>
      @if (titlePrice) {
        <p class="text-body-16 Heading-price font-medium text-right">
          {{ titlePrice | flCurrency: currency.code }}
        </p>
      }
    </div>
  `,
  styleUrls: ['./showcase-card-title.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ShowcaseCardTitleComponent {
  @Input() maxLines?: number;
  @Input() titlePrice?: number;
  @Input() currency: Currency;
}

@Component({
  standalone: false,
  selector: 'fl-showcase-card-description',
  template: `
    <p class="text-body-16">
      <ng-content></ng-content>
    </p>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ShowcaseCardDescriptionComponent {}
@Component({
  standalone: false,
  selector: 'fl-showcase-card-expandable-content',
  template: ` <ng-content></ng-content> `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ShowcaseCardExpandableContentComponent {}

@Component({
  standalone: false,
  selector: 'fl-showcase-card-carousel-item',
  template: ``,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ShowcaseCardCarouselItemComponent {
  @Input() src: string;
  @Input() alt: string;

  @ContentChild('imageCaption')
  imageCaption: TemplateRef<any>;
}

@Component({
  standalone: false,
  selector: 'fl-showcase-card',
  template: `
    <div
      class="CardContainer"
      [class.ScaleOnHover]="scaleOnHover"
      [class.ToggleBorderRadius]="showcaseCardExpandableContentComponent"
    >
      <div
        flTrackingSection="ShowcaseCardSection"
        class="ImageContainer"
      >
        @if (!link) {
          <ng-template [ngTemplateOutlet]="content"></ng-template>
        } @else {
          <fl-button
            flTrackingLabel="ShowcaseCard"
            [display]="'block'"
            [link]="link"
            [newTab]="newTab"
          >
            <ng-template [ngTemplateOutlet]="content"></ng-template>
          </fl-button>
        }

        <ng-template #content>
          @if ((isImage && !error) || carousel) {
            @if (showLikeAction || showIconAction) {
              <div class="IconWrapper">
                @if (showLikeAction) {
                  <fl-icon
                    i18n-label="Like button text"
                    label="Like"
                    class="{{ showIconAction ? 'mr-xxsmall' : 'mr-none' }}"
                    [backdrop]="IconBackdrop.DARK"
                    [backdropStyle]="IconBackdropStyle.TRANSPARENT"
                    [clickable]="true"
                    [color]="IconColor.LIGHT"
                    [name]="'ui-heart'"
                    [useIconFont]="true"
                    [fillIconFont]="isLikeActive"
                    (click)="handleLikeClick()"
                  >
                  </fl-icon>
                }
                @if (showIconAction) {
                  <fl-icon
                    [label]="labelIconAction"
                    [backdrop]="IconBackdrop.DARK"
                    [backdropStyle]="IconBackdropStyle.TRANSPARENT"
                    [clickable]="true"
                    [color]="IconColor.LIGHT"
                    [name]="'ui-share'"
                    [useIconFont]="true"
                    (click)="handleActionClick()"
                  >
                  </fl-icon>
                }
              </div>
            }
            @if (carousel) {
              <fl-carousel
                [currentIndex]="0"
                [showDotIndicator]="true"
              >
                <fl-carousel-prev-btn>
                  <fl-icon
                    class="Control"
                    [name]="'ui-arrow-back'"
                    [size]="IconSize.SMALL"
                    [clickable]="true"
                    [backdrop]="IconBackdrop.WHITE"
                    [backdropStyle]="IconBackdropStyle.TRANSPARENT"
                    [backdropSizing]="'standard'"
                    [useIconFont]="true"
                  ></fl-icon>
                </fl-carousel-prev-btn>
                @for (
                  item of carouselItems;
                  track trackByCarouselItem($index, item)
                ) {
                  <fl-carousel-item>
                    <fl-picture
                      class="Picture"
                      [alignCenter]="true"
                      [alt]="item.alt"
                      [borderRadiusTop]="scaleOnHover"
                      [borderRadiusBottom]="borderRadiusBottom"
                      [display]="PictureDisplay.BLOCK"
                      [fullWidth]="fullWidth"
                      [layout]="PictureLayout.INTRINSIC"
                      [loadMethod]="PictureLoadMethod.LAZY"
                      [objectFit]="PictureObjectFit.COVER"
                      [pictureMetadata]="pictureMetadata"
                      [fixedAspectRatio]="true"
                      [src]="item.src"
                      [width]="imageIntrinsicWidth"
                    ></fl-picture>
                  </fl-carousel-item>
                }
                <fl-carousel-next-btn>
                  <fl-icon
                    class="Control"
                    [name]="'ui-arrow-forward'"
                    [size]="IconSize.SMALL"
                    [backdrop]="IconBackdrop.WHITE"
                    [backdropStyle]="IconBackdropStyle.TRANSPARENT"
                    [backdropSizing]="'standard'"
                    [clickable]="true"
                    [useIconFont]="true"
                  ></fl-icon>
                </fl-carousel-next-btn>
              </fl-carousel>
            } @else {
              <fl-picture
                [alignCenter]="true"
                [alt]="fileAlt"
                [borderRadiusTop]="scaleOnHover"
                [borderRadiusBottom]="borderRadiusBottom"
                [display]="PictureDisplay.BLOCK"
                [fixedAspectRatio]="
                  !aspectRatioValue
                    ? variant === ShowcaseCardVariant.LANDING_PAGE
                    : false
                "
                [fullWidth]="fullWidth"
                [layout]="PictureLayout.INTRINSIC"
                [loadMethod]="PictureLoadMethod.LAZY"
                [aspectRatioValue]="aspectRatioValue"
                [class.ImageContainer-showcaseVariant]="
                  variant === ShowcaseCardVariant.SHOWCASE_PAGE
                "
                [objectFit]="PictureObjectFit.COVER"
                [pictureMetadata]="pictureMetadata"
                [src]="fileSrc"
                [width]="imageIntrinsicWidth"
                (imageLoaded)="imageLoaded = true"
                (onError)="error = true"
              ></fl-picture>
            }
          } @else {
            <div class="DefaultTemplate">
              <fl-file-display
                class="DefaultTemplate-icon"
                [src]="fileSrc"
                [iconSize]="IconSize.XXLARGE"
                [iconColor]="IconColor.LIGHT"
                [type]="FileDisplayType.ICON"
              ></fl-file-display>
            </div>
          }
          @if (!error && !imageLoaded && isImage && !carousel) {
            <fl-loading-template class="ImageContainer-loading" />
          }
        </ng-template>

        @if (variant === ShowcaseCardVariant.SHOWCASE_PAGE) {
          <div
            [class]="{ 'ImageContainer-hoverItems': !keepHoverStateShown }"
            [class.desktop:show]="true"
          >
            <ng-content select="fl-showcase-card-hover-content"></ng-content>
          </div>
        }
      </div>
      <div class="ShowcaseTitle">
        @if (link) {
          <fl-link
            [link]="link"
            [newTab]="newTab"
            [underline]="LinkUnderline.NEVER"
          >
            <ng-container [ngTemplateOutlet]="title"></ng-container>
          </fl-link>
        } @else {
          <ng-container [ngTemplateOutlet]="title"></ng-container>
        }

        <ng-template #title>
          <ng-content select="fl-showcase-card-title"></ng-content>
        </ng-template>

        @if (variant === ShowcaseCardVariant.SHOWCASE_PAGE) {
          <ng-content select="fl-showcase-likes"></ng-content>
        }
      </div>
      @if (variant === ShowcaseCardVariant.LANDING_PAGE) {
        @if (showcaseCardDescriptionComponent) {
          <div class="Description">
            <ng-content select="fl-showcase-card-description"></ng-content>
          </div>
        }
      }
      @if (showcaseCardExpandableContentComponent) {
        <div class="ContentWrapper">
          <div class="Content">
            <ng-content
              select="fl-showcase-card-expandable-content"
            ></ng-content>
          </div>
        </div>
      }
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrls: ['./showcase-card.component.scss'],
})
export class ShowcaseCardComponent
  implements AfterViewInit, OnDestroy, OnChanges
{
  FileDisplayType = FileDisplayType;
  IconColor = IconColor;
  IconSize = IconSize;
  IconBackdropStyle = IconBackdropStyle;
  IconBackdrop = IconBackdrop;
  LinkUnderline = LinkUnderline;
  PictureDisplay = PictureDisplay;
  PictureLayout = PictureLayout;
  PictureLoadMethod = PictureLoadMethod;
  PictureObjectFit = PictureObjectFit;
  ShowcaseCardVariant = ShowcaseCardVariant;

  imageLoaded = false;
  error = false;
  isImage = true;
  isLikeActive = false;

  @Input() fullWidth = true;
  @Input() titlePrice: number;
  @Input() fileAlt: string;
  @Input() fileSrc: string;
  @Input() link?: string;
  @Input() newTab = false;
  @Input() keepHoverStateShown = false;
  @Input() pictureMetadata?: PictureMetadata;
  @Input() imageIntrinsicWidth?: number;
  @Input() borderRadiusBottom = false;

  /**
   * Set the aspect ratio of the image on the showcase card
   */
  @Input() aspectRatioValue?: AspectRatioValue;

  /**
   * Shows action icons on the top-right side of the card
   */
  @Input() showLikeAction = false;
  /** When true, shows the share icon action button. */
  @Input() showIconAction = false;
  @Input() labelIconAction: string;

  @Input() scaleOnHover = false;
  @Input() carousel = false;

  @Input() variant: ShowcaseCardVariant = ShowcaseCardVariant.LANDING_PAGE;
  /**
   * Set the height of the image for showcase variant in order to keep
   * the masonry style on showcase work
   */
  @HostBinding('style.--image-desktop-xxxxlarge-height')
  @Input()
  imageXXXXLargeHeight?: string;

  @HostBinding('style.--image-desktop-xxxlarge-height')
  @Input()
  imageXXXLargeHeight?: string;

  @HostBinding('style.--image-desktop-xxlarge-height')
  @Input()
  imageXXLargeHeight?: string;

  @HostBinding('style.--image-desktop-xlarge-height')
  @Input()
  imageXLargeHeight?: string;

  @HostBinding('style.--image-desktop-large-height')
  @Input()
  imageLargeHeight?: string;

  @HostBinding('style.--image-desktop-height')
  @Input()
  imageHeight?: string;

  @HostBinding('style.--image-desktop-small-height')
  @Input()
  imageDesktopSmallHeight?: string;

  @HostBinding('style.--image-tablet-height')
  @Input()
  imageTabletHeight?: string;

  @HostBinding('style.--image-mobile-large-height')
  @Input()
  imageMobileLargeHeight?: string;

  @HostBinding('style.--image-mobile-height')
  @Input()
  imageMobileHeight = '100%';

  @Output() onClickLike = new EventEmitter<void>();
  @Output() onClickAction = new EventEmitter<void>();

  @ContentChild(ShowcaseCardExpandableContentComponent)
  showcaseCardExpandableContentComponent: ShowcaseCardExpandableContentComponent;

  @ContentChild(ShowcaseCardDescriptionComponent)
  showcaseCardDescriptionComponent: ShowcaseCardDescriptionComponent;

  @ContentChildren(ShowcaseCardCarouselItemComponent)
  carouselItems: QueryList<ShowcaseCardCarouselItemComponent>;

  // FIXME: T300116 Enable hydration. Currently fails on client side with
  // TypeError: Cannot read properties of null (reading 'firstChild')
  @HostBinding('attr.ngSkipHydration') ngSkipHydration = true;

  private _indexSubject$ = new BehaviorSubject(0);
  index$ = this._indexSubject$.asObservable();

  private subscriptions = new Subscription();

  ngAfterViewInit(): void {
    // Reset the index to 0 when number of carousel items has changed
    this.subscriptions.add(
      this.carouselItems.changes.subscribe(item => {
        this._indexSubject$.next(0);
      }),
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ('fileSrc' in changes) {
      this.isImage = isImageFile(this.fileSrc);
    }
  }

  handleLikeClick(): void {
    this.isLikeActive = !this.isLikeActive;
    this.onClickLike.emit();
  }

  handleActionClick(): void {
    this.onClickAction.emit();
  }

  trackByCarouselItem(
    _: number,
    item: ShowcaseCardCarouselItemComponent,
  ): string {
    return `${item.src}:${item.alt}`;
  }
}
